import { Button } from '@/components/ui/button';
import { Pin, PinOff, Reply, Trash2, Flag, Star, CornerDownRight } from 'lucide-react';
import { EmojiPickerButton, EmojiReactions } from '@/components/EmojiPicker';
import MentionText from '@/components/MentionText';
import TruncatedText from '@/components/TruncatedText';
import ProfilePicture from '@/components/ProfilePicture';
import { UserProfilePopover } from '@/components/UserProfilePopover';
import TranslateButton from '@/components/TranslateButton';

const timeAgo = (iso) => {
  try {
    const d = new Date(iso);
    const diff = (Date.now() - d.getTime()) / 1000;
    if (diff < 60) return 'just now';
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    if (diff < 604800) return `${Math.floor(diff / 86400)}d ago`;
    return d.toLocaleDateString();
  } catch {
    return '';
  }
};

/**
 * A single chat message bubble (Phase 5 modern chat redesign).
 */
export const ChatMessage = ({
  post,
  user,
  tr,
  language,
  isLead,
  isConnected,
  canPin,
  isViewOnly,
  onReact,
  onPin,
  onUnpin,
  onReply,
  onDelete,
  onReport,
  compact = false,
}) => {
  const isOwn = post.user_id === user.id;
  const isAdmin = user.role === 'admin';

  return (
    <div
      className={`group flex gap-3 rounded-xl px-3 py-2.5 transition-colors ${
        post.is_pinned && !compact ? 'bg-amber-50/70 hover:bg-amber-50' : 'hover:bg-cyan-50/60'
      }`}
      data-testid={`chat-message-${post.id}`}
    >
      <UserProfilePopover userId={post.user_id}>
        <ProfilePicture src={post.profile_picture} alt={post.username} size="sm" />
      </UserProfilePopover>

      <div className="flex-1 min-w-0">
        {/* Header */}
        <div className="flex items-center gap-2 flex-wrap">
          <span className="font-semibold text-gray-900 text-sm">{post.username}</span>
          {isLead && (
            <span
              className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wide bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded"
              data-testid={`section-lead-badge-${post.id}`}
            >
              <Star className="w-3 h-3 fill-amber-500 text-amber-500" /> Section Lead
            </span>
          )}
          {isConnected && (
            <span title="Connected" className="text-xs" data-testid={`connected-marker-${post.user_id}`}>
              🤝
            </span>
          )}
          <span className="text-xs text-gray-400">{timeAgo(post.created_at)}</span>
          {post.is_pinned && (
            <span className="inline-flex items-center gap-1 text-[10px] text-amber-600 font-medium">
              <Pin className="w-3 h-3" /> Pinned
            </span>
          )}

          {/* Hover actions */}
          {!compact && (
            <div className="ml-auto flex items-center gap-0.5 opacity-100 sm:opacity-0 sm:group-hover:opacity-100 focus-within:opacity-100 transition-opacity">
              {!isViewOnly && (
                <EmojiPickerButton onEmojiSelect={(emoji) => onReact(post.id, emoji)} />
              )}
              {!isViewOnly && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-7 w-7 p-0 text-gray-400 hover:text-cyan-600"
                  title="Reply"
                  onClick={() => onReply(post)}
                  data-testid={`chat-reply-btn-${post.id}`}
                >
                  <Reply className="w-4 h-4" />
                </Button>
              )}
              {canPin && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-7 w-7 p-0 text-gray-400 hover:text-amber-600"
                  title={post.is_pinned ? 'Unpin' : 'Pin'}
                  onClick={() => (post.is_pinned ? onUnpin(post.id) : onPin(post.id))}
                  data-testid={`chat-pin-btn-${post.id}`}
                >
                  {post.is_pinned ? <PinOff className="w-4 h-4" /> : <Pin className="w-4 h-4" />}
                </Button>
              )}
              {!isViewOnly && !isOwn && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-7 w-7 p-0 text-gray-400 hover:text-orange-600"
                  title={isAdmin ? 'Flag for moderation' : 'Report this message'}
                  onClick={() => onReport(post.id)}
                  data-testid={`chat-report-btn-${post.id}`}
                >
                  <Flag className="w-4 h-4" />
                </Button>
              )}
              {isAdmin && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-7 w-7 p-0 text-gray-400 hover:text-red-600"
                  title="Delete"
                  onClick={() => onDelete(post.id)}
                  data-testid={`chat-delete-btn-${post.id}`}
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              )}
            </div>
          )}
        </div>

        {/* Quote / reply-to */}
        {post.reply_to && (
          <div className="mt-1 mb-1 flex items-start gap-1.5 text-xs text-gray-500 border-l-2 border-cyan-300 pl-2 py-0.5 bg-gray-50 rounded-r">
            <CornerDownRight className="w-3 h-3 mt-0.5 flex-shrink-0 text-cyan-400" />
            <span className="min-w-0">
              <span className="font-medium text-cyan-700">{post.reply_to.username}</span>{' '}
              <span className="line-clamp-1">{post.reply_to.snippet}</span>
            </span>
          </div>
        )}

        {/* Title (legacy posts render title as a bold first line) */}
        {post.title && post.title.trim() && (
          <p className="font-semibold text-gray-900 mt-0.5">{tr(post.title)}</p>
        )}

        {/* Body */}
        {post.content && (
          <div className="text-gray-700 text-sm mt-0.5">
            <TruncatedText text={tr(post.content)} maxLength={600} className="whitespace-pre-wrap">
              {(text) => <MentionText>{text}</MentionText>}
            </TruncatedText>
          </div>
        )}

        {post.content && language === 'en' && !compact && (
          <TranslateButton text={post.content} className="mt-1" />
        )}

        {/* Legacy homework attachment (read-only; homework is now decoupled into modules) */}
        {post.homework_file && (
          <div className="mt-2">
            {post.homework_is_video ? (
              <video controls className="w-full max-w-md rounded-lg" style={{ maxHeight: '320px' }}>
                <source src={`${process.env.REACT_APP_BACKEND_URL}${post.homework_file}`} />
              </video>
            ) : (
              <a
                href={`${process.env.REACT_APP_BACKEND_URL}${post.homework_file}`}
                target="_blank"
                rel="noopener noreferrer"
                className="text-sm text-purple-600 hover:text-purple-800 underline"
              >
                📎 {post.homework_filename || 'Attachment'}
              </a>
            )}
          </div>
        )}

        {/* Reactions */}
        {(Object.keys(post.reactions || {}).length > 0 || !compact) && (
          <div className="mt-2">
            <EmojiReactions
              reactions={post.reactions || {}}
              onReact={isViewOnly ? null : (emoji) => onReact(post.id, emoji)}
              currentUserId={user.id}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default ChatMessage;
